'use client';

import type { Descendant } from './useDescendants';

export function isElementDisabled(element: HTMLElement) {
  return (
    element.hasAttribute('disabled') ||
    element.getAttribute('aria-disabled') === 'true' ||
    element.hasAttribute('data-disabled')
  );
}

export function getChildIndexById(id: string, descendants: Array<Descendant>) {
  return descendants.findIndex((descendant) => descendant.id === id);
}

export function getChildById(id: string, descendants: Array<Descendant>) {
  return descendants.find((descendant) => descendant.id === id);
}

/**
 * Find the first enabled descendant.
 */

export function getFirstEnabledChildIndex(descendants: Array<Descendant>) {
  return descendants.findIndex(
    (descendant) => !isElementDisabled(descendant.element.current)
  );
}

/**
 * Find the last enabled descendant.
 */

export function getLastEnabledChildIndex(descendants: Array<Descendant>) {
  // eslint-disable-next-line no-plusplus
  for (let i = descendants.length - 1; i >= 0; i--) {
    if (!isElementDisabled(descendants[i].element.current)) {
      return i;
    }
  }

  return -1;
}

/**
 * Find the next enabled child.
 */

export function getNextEnabledChild(
  currentId: string | null,
  descendants: Array<Descendant>
) {
  if (!currentId) {
    return descendants[getFirstEnabledChildIndex(descendants)];
  }

  const currentIndex = descendants.findIndex(({ id }) => id === currentId);
  const nextIndex = getNextEnabledIndex(currentIndex, descendants);
  return descendants[nextIndex];
}

/**
 * Using an index, get the next enabled child
 */

export function getNextEnabledIndex(
  index: number,
  descendants: Array<Descendant>
): number {
  const nextIndex = Math.min(index + 1, getLastEnabledChildIndex(descendants));
  return isElementDisabled(descendants[nextIndex].element.current)
    ? getNextEnabledIndex(nextIndex, descendants)
    : nextIndex;
}

/**
 * Find the previously enabled child.
 */

export function getPrevEnabledChild(
  currentId: string | null,
  descendants: Array<Descendant>
) {
  if (!currentId) {
    return descendants[getLastEnabledChildIndex(descendants)];
  }

  const currentIndex = descendants.findIndex(({ id }) => id === currentId);
  const prevIndex = getPrevEnabledIndex(currentIndex, descendants);
  return descendants[prevIndex];
}

/**
 * Find the previously enabled child given an index
 */

export function getPrevEnabledIndex(
  index: number,
  descendants: Array<Descendant>
): number {
  const prevIndex = Math.max(index - 1, getFirstEnabledChildIndex(descendants));
  return isElementDisabled(descendants[prevIndex].element.current)
    ? getPrevEnabledIndex(prevIndex, descendants)
    : prevIndex;
}

/**
 * Find the first element that contains text which starts
 * with the provided query. Used for typeahead functionality.
 */

export function findItemFromTypeahead(
  descendants: Array<Descendant>,
  query = ''
) {
  if (!query) {
    return null;
  }

  return descendants.find(
    ({ element }) =>
      element.current.textContent.toLowerCase().startsWith(query) &&
      !isElementDisabled(element.current)
  );
}

export function hasDefaultSelected(descendants: Array<Descendant>) {
  return Boolean(
    descendants.find((descendant) => descendant.defaultSelected === true)
  );
}
